import { Form } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'ui';
import { InputNumber } from 'ui/InputNumber';
import { useIntl } from 'react-intl';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { StyledDeliverySection, StyledDeliveryManagement } from './style';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { DeliveryCondition, Fulfillment, ItemProps } from '../../../../../interfaces/BackendModels';
import { ProductsAvailabilityTable } from './ProductsAvailabilityTable';
import { FulfillmentCard } from './FulfillmentCard';
import { DeliveryConditionDetails } from './DeliveryConditionDetails';
import { FlattenedFulfillment, FulfillmentUpdateData, DeliverySectionProps, FulfillmentFormProduct } from './types';
import { useState, useMemo, useCallback, useEffect } from 'react';

const SELLER_ACTION_STATUSES = ['pending_send', 'partially_sent', 'sent', 'delivered'];
const DELIVERY_SECTION_STATUSES = [...SELLER_ACTION_STATUSES, 'confirmed'];
const EMPTY_DELIVERY_CONDITIONS: DeliveryCondition[] = [];
const EMPTY_FULFILLMENTS: Fulfillment[] = [];

export const DeliverySection = ({
  isEditMode = false,
  addressOnly = false,
  transactionStatus,
  deliveryConditions = EMPTY_DELIVERY_CONDITIONS,
  fulfillments: directFulfillments = EMPTY_FULFILLMENTS,
  transactionId,
  onCreateFulfillment,
  onUpdateFulfillment,
  onMarkFulfillmentSent,
  onDeleteFulfillment,
  onSubmitFulfillment,
  onSendFulfillmentInvoice,
  onConfirmFulfillmentPayment,
  onDownloadFulfillmentInvoice,
  isWholesale = false,
  items = [],
  isProvider = false,
}: DeliverySectionProps) => {
  const intl = useIntl();
  const form = useFormInstance();
  const [localProductUpdates, setLocalProductUpdates] = useState<Map<number, FulfillmentFormProduct[]>>(new Map());

  const deliveries = Form.useWatch('deliveries', form) || [];

  const conditionForFieldIndex = useCallback(
    (index: number): DeliveryCondition | undefined => {
      const conditionId = deliveries[index]?.id;
      return conditionId
        ? deliveryConditions.find((dc) => dc.id === conditionId)
        : undefined;
    },
    [deliveries, deliveryConditions]
  );

  useEffect(() => {
    setLocalProductUpdates(new Map());
  }, [directFulfillments, deliveryConditions]);

  const normalizedStatus = transactionStatus?.toLowerCase() ?? '';
  const isDeliveryManagementMode =
    isWholesale && DELIVERY_SECTION_STATUSES.includes(normalizedStatus);
  const canSellerAct = isProvider && SELLER_ACTION_STATUSES.includes(normalizedStatus);

  const flattenedFulfillments = useMemo((): FlattenedFulfillment[] => {
    const seenIds = new Set<number>();
    const result: FlattenedFulfillment[] = [];
    let globalIndex = 0;

    directFulfillments.forEach((f) => {
      seenIds.add(f.id);
      result.push({
        ...f,
        deliveryConditionId: f.delivery_condition_id ?? 0,
        deliveryAddress: f.delivery_address ?? null,
        globalIndex: globalIndex++,
      });
    });

    deliveryConditions.forEach((dc) =>
      (dc.fulfillments || []).forEach((f) => {
        if (!seenIds.has(f.id)) {
          seenIds.add(f.id);
          result.push({
            ...f,
            deliveryConditionId: dc.id,
            deliveryAddress: dc.delivery_address,
            globalIndex: globalIndex++,
          });
        }
      })
    );

    return result;
  }, [directFulfillments, deliveryConditions]);

  const getTotalQuantityForProduct = useCallback(
    (productId: number): number => {
      return items
        .filter((i) => i.product_id === productId)
        .reduce((sum, i) => sum + Number(i.stock_count ?? 0), 0);
    },
    [items]
  );

  const getAssignedQuantityForProduct = useCallback(
    (productId: number): number => {
      const productItemIds = items.filter((i) => i.product_id === productId).map((i) => i.id);
      let assigned = 0;
      flattenedFulfillments.forEach((f) => {
        if (isProvider && f.is_draft) return;
        const localProducts = localProductUpdates.get(f.id);
        if (localProducts) {
          localProducts.forEach((p) => {
            if (productItemIds.includes(p.order_item_id)) {
              assigned += Number(p.product_quantity ?? 0);
            }
          });
        } else {
          f.products?.forEach((p) => {
            const orderItemId = p.id ?? p.order_item_id;
            if (productItemIds.includes(orderItemId)) {
              assigned += Number(p.pivot?.product_quantity ?? p.product_quantity ?? 0);
            }
          });
        }
      });
      return assigned;
    },
    [items, flattenedFulfillments, localProductUpdates, isProvider]
  );

  const getRemainingQuantityForProduct = useCallback(
    (productId: number): number => {
      return getTotalQuantityForProduct(productId) - getAssignedQuantityForProduct(productId);
    },
    [getTotalQuantityForProduct, getAssignedQuantityForProduct]
  );

  const getMaxQuantityForProduct = useCallback(
    (fulfillmentId: number, orderItemId: number): number => {
      const item = items.find((i) => i.id === orderItemId);
      if (!item) return 0;

      const productId = item.product_id;
      const remainingForProduct = getRemainingQuantityForProduct(productId);

      let assignedInCurrentFulfillment = 0;
      const localProducts = localProductUpdates.get(fulfillmentId);
      if (localProducts) {
        const productItemIds = items.filter((i) => i.product_id === productId).map((i) => i.id);
        localProducts.forEach((p) => {
          if (productItemIds.includes(p.order_item_id)) {
            assignedInCurrentFulfillment += Number(p.product_quantity ?? 0);
          }
        });
      } else {
        const fulfillment = flattenedFulfillments.find((f) => f.id === fulfillmentId);
        if (fulfillment?.products) {
          const productItemIds = items.filter((i) => i.product_id === productId).map((i) => i.id);
          fulfillment.products.forEach((p) => {
            const itemId = p.id ?? p.order_item_id;
            if (productItemIds.includes(itemId)) {
              assignedInCurrentFulfillment += Number(p.pivot?.product_quantity ?? p.product_quantity ?? 0);
            }
          });
        }
      }

      return remainingForProduct + assignedInCurrentFulfillment;
    },
    [items, localProductUpdates, flattenedFulfillments, getRemainingQuantityForProduct]
  );

  const getAvailableProducts = useCallback(
    (fulfillmentId: number): ItemProps[] => {
      const fulfillment = flattenedFulfillments.find((f) => f.id === fulfillmentId);
      const localProducts = localProductUpdates.get(fulfillmentId);

      const productIdsInFulfillment = new Set<number>();
      if (localProducts) {
        localProducts.forEach((p) => {
          const item = items.find((i) => i.id === p.order_item_id);
          if (item) productIdsInFulfillment.add(item.product_id);
        });
      } else {
        fulfillment?.products?.forEach((p) => {
          const orderItemId = p.id ?? p.order_item_id;
          const item = items.find((i) => i.id === orderItemId);
          if (item) productIdsInFulfillment.add(item.product_id);
        });
      }

      const seenProductIds = new Set<number>();
      return items.filter((item) => {
        if (seenProductIds.has(item.product_id)) return false;
        seenProductIds.add(item.product_id);
        if (productIdsInFulfillment.has(item.product_id)) return false;
        return getRemainingQuantityForProduct(item.product_id) > 0;
      });
    },
    [flattenedFulfillments, items, getRemainingQuantityForProduct, localProductUpdates]
  );

  const canMarkFulfillmentAsSent = useCallback(
    (fulfillment: Fulfillment): boolean => {
      if (fulfillment.status === 'sent') return false;

      const localProducts = localProductUpdates.get(fulfillment.id);
      if (localProducts) {
        return localProducts.some(
          (p) => p.order_item_id !== undefined && p.product_quantity > 0
        );
      }
      return (fulfillment.products?.length ?? 0) > 0;
    },
    [localProductUpdates]
  );

  const handleUpdateFulfillment = useCallback(
    (fulfillmentId: number, data: FulfillmentUpdateData) => {
      if (data.products) {
        setLocalProductUpdates((prev) => {
          const next = new Map(prev);
          next.set(fulfillmentId, data.products as FulfillmentFormProduct[]);
          return next;
        });
      }
      if (onUpdateFulfillment) {
        onUpdateFulfillment(fulfillmentId, data);
      }
    },
    [onUpdateFulfillment]
  );

  const handleMarkFulfillmentSent = useCallback(
    (fulfillmentId: number) => {
      onMarkFulfillmentSent?.(fulfillmentId);
    },
    [onMarkFulfillmentSent]
  );

  const handleDeleteFulfillment = useCallback(
    (fulfillmentId: number) => {
      onDeleteFulfillment?.(fulfillmentId);
    },
    [onDeleteFulfillment]
  );

  const handleAddFulfillment = useCallback(() => {
    if (transactionId && onCreateFulfillment) {
      onCreateFulfillment(transactionId);
    }
  }, [transactionId, onCreateFulfillment]);

  const allProductsAssigned = useMemo(() => {
    return items.every((item) => getRemainingQuantityForProduct(item.product_id) <= 0);
  }, [items, getRemainingQuantityForProduct]);

  const visibleFulfillments = useMemo(() => {
    if (isProvider) {
      return flattenedFulfillments.filter((f) => !f.is_draft);
    }
    return flattenedFulfillments;
  }, [flattenedFulfillments, isProvider]);

  const sentFulfillments = useMemo(() => {
    return visibleFulfillments.filter((f) => f.status === 'sent');
  }, [visibleFulfillments]);

  const pendingFulfillments = useMemo(() => {
    return visibleFulfillments.filter((f) => f.status !== 'sent');
  }, [visibleFulfillments]);

  const allProductsSent = useMemo(() => {
    const getSentQuantityForProduct = (productId: number): number => {
      const productItemIds = items.filter((i) => i.product_id === productId).map((i) => i.id);
      let sent = 0;
      flattenedFulfillments.forEach((f) => {
        if (f.status === 'sent') {
          f.products?.forEach((p) => {
            const orderItemId = p.id ?? p.order_item_id;
            if (orderItemId !== undefined && productItemIds.includes(orderItemId)) {
              sent += Number(p.pivot?.product_quantity ?? p.product_quantity ?? 0);
            }
          });
        }
      });
      return sent;
    };

    return items.every((item) => {
      const total = getTotalQuantityForProduct(item.product_id);
      const sent = getSentQuantityForProduct(item.product_id);
      return sent >= total;
    });
  }, [items, flattenedFulfillments, getTotalQuantityForProduct]);

  const hasFulfillments = flattenedFulfillments.length > 0;

  const isConfirmedWithoutFulfillments =
    normalizedStatus === 'confirmed' && !hasFulfillments;

  if (isDeliveryManagementMode && (hasFulfillments || transactionId) && !isConfirmedWithoutFulfillments) {
    return (
      <StyledDeliveryManagement>
        {!allProductsAssigned && (
          <ProductsAvailabilityTable
            items={items}
            getRemainingQuantityForProduct={getRemainingQuantityForProduct}
          />
        )}

        {sentFulfillments.length > 0 && (
          <>
            <h4 className="section-title">
              {intl.formatMessage({ id: 'fulfillment.sent_products' })}:
            </h4>
            <div className="fulfillments-list">
              {sentFulfillments.map((fulfillment) => (
                <FulfillmentCard
                  key={fulfillment.id}
                  fulfillment={fulfillment}
                  items={items}
                  getAvailableProducts={getAvailableProducts}
                  getRemainingQuantityForProduct={getRemainingQuantityForProduct}
                  getMaxQuantityForProduct={getMaxQuantityForProduct}
                  onUpdateFulfillment={handleUpdateFulfillment}
                  onMarkFulfillmentSent={handleMarkFulfillmentSent}
                  onDeleteFulfillment={handleDeleteFulfillment}
                  onSubmitFulfillment={onSubmitFulfillment}
                  onSendFulfillmentInvoice={onSendFulfillmentInvoice}
                  onConfirmFulfillmentPayment={onConfirmFulfillmentPayment}
                  onDownloadFulfillmentInvoice={onDownloadFulfillmentInvoice}
                  canMarkFulfillmentAsSent={canMarkFulfillmentAsSent}
                  isProvider={isProvider}
                  canSellerAct={canSellerAct}
                />
              ))}
            </div>
          </>
        )}

        {pendingFulfillments.length > 0 && (
          <div className="fulfillments-list">
            {pendingFulfillments.map((fulfillment) => (
              <FulfillmentCard
                key={fulfillment.id}
                fulfillment={fulfillment}
                items={items}
                getAvailableProducts={getAvailableProducts}
                getRemainingQuantityForProduct={getRemainingQuantityForProduct}
                getMaxQuantityForProduct={getMaxQuantityForProduct}
                onUpdateFulfillment={handleUpdateFulfillment}
                onMarkFulfillmentSent={handleMarkFulfillmentSent}
                onDeleteFulfillment={handleDeleteFulfillment}
                onSubmitFulfillment={onSubmitFulfillment}
                onSendFulfillmentInvoice={onSendFulfillmentInvoice}
                onConfirmFulfillmentPayment={onConfirmFulfillmentPayment}
                onDownloadFulfillmentInvoice={onDownloadFulfillmentInvoice}
                canMarkFulfillmentAsSent={canMarkFulfillmentAsSent}
                isProvider={isProvider}
                canSellerAct={canSellerAct}
              />
            ))}
          </div>
        )}

        {!allProductsSent && !allProductsAssigned && !isProvider && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddFulfillment}
            className="add-fulfillment-button"
            label={intl.formatMessage({ id: 'fulfillment.add' })}
          />
        )}
      </StyledDeliveryManagement>
    );
  }

  if (!isEditMode) {
    return null;
  }

  return (
    <StyledDeliverySection>
      <Form.List name="deliveries">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <div key={field.key} className="delivery-entry">
                <div className="delivery-row">
                  <div className="delivery-fields">
                    <div className="address-field">
                      <WithoutModalAddressFinder
                        form={form}
                        name={[field.name, 'delivery_address']}
                        fullPath={['deliveries', field.name, 'delivery_address']}
                        label={intl.formatMessage({ id: 'invoice.delivery_address' })}
                        layout="vertical"
                        disabled={false}
                        deliveryTypeValue="delivery"
                      />
                    </div>

                    {!addressOnly && (
                      <div className="price-field">
                        <InputNumber
                          name={[field.name, 'delivery_price']}
                          label={intl.formatMessage({ id: 'invoice.delivery_price' })}
                          layout="vertical"
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({ id: 'general.required' }),
                            },
                          ]}
                          min={0}
                          controls={false}
                        />
                      </div>
                    )}
                  </div>

                  {!addressOnly && (
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => remove(field.name)}
                      size="small"
                      className="delete-button"
                    />
                  )}
                </div>

                <DeliveryConditionDetails condition={conditionForFieldIndex(field.name)} />
              </div>
            ))}

            {deliveries.length === 0 && (
              <div className="empty-state">
                {intl.formatMessage({ id: 'general.no_delivery_added' })}
              </div>
            )}

            {!addressOnly && (
              <div className="delivery-footer">
                <Button
                  onClick={() => add({ delivery_address: '', delivery_price: 0 })}
                  className="add-delivery-btn"
                  label={intl.formatMessage({ id: 'general.add_delivery' })}
                />
              </div>
            )}
          </>
        )}
      </Form.List>
    </StyledDeliverySection>
  );
};
